import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppState } from "@Store/index";
import { applyFilter } from "../data/searchPageReducer";
import formatFilterState from "../utils/formatFilterState";
import { filter_keys } from "../data/helper";
import isValidMoveInMonth from "../utils/isValidMoveInMonth";
import { getLocation } from "@Pages/PropertyPage/data/helper";
import AmberlocalStorage from "@Utils/AmberlocalStorage";

/**
 * Custom hook to handle browser history navigation for search page filters.
 *
 * How it works:
 * - When a user applies a filter on the search page, `applyFilter` (in searchPageReducer)
 *   calls `window.history.pushState(...)` to add a new history entry with the filter state.
 * - When the user presses the browser back button, a `popstate` event fires.
 * - This hook listens for `popstate` and re-applies the filters from the previous
 *   history entry, effectively "undoing" the last filter change.
 *
 * Key edge cases handled:
 * 1. Modal/bottom sheet closes: Components like BottomSheetOverlay use `useModal` which
 *    also pushes history entries (for back-to-close behavior). When those close via
 *    `history.goBack()`, a `popstate` fires but the URL doesn't change (same pathname+search).
 *    We detect this by comparing `lastSearchUrl` and skip the refetch to avoid skeleton flicker.
 *
 * 2. Property page scope: This hook is only mounted on search pages, but the `popstate`
 *    listener is global. If the user navigates to a property page and presses back,
 *    the popstate fires before the search component remounts. We guard against this by
 *    checking `window.location.pathname` matches a search page URL pattern.
 *
 * 3. Rapid back-navigation timing: `lastSearchUrl` is updated via a React `useEffect`
 *    (which is async). If the user presses back faster than React can re-render, the ref
 *    could be stale. We fix this by monkey-patching `pushState`/`replaceState` to update
 *    `lastSearchUrl` synchronously whenever the URL changes.
 */
const useFilterHistory = () => {
  const dispatch = useDispatch();
  const { currentFilters, region } = useSelector(({ searchPageData }: AppState) => ({
    currentFilters: searchPageData.filters,
    region: searchPageData.region,
  }));

  // Prevent re-entrant popstate handling (e.g. if applyFilter triggers another
  // history change while we're still processing the current one).
  const isHandlingPopState = useRef(false);

  // Track whether the Search component is mounted. The popstate listener is
  // global — it can fire even after the component unmounts (e.g. user navigated
  // to property page and pressed back). We use this ref instead of checking
  // window.location.pathname with a regex, which would be fragile.
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Store current filters in a ref so the popstate event handler (which is a
  // closure created once) always has access to the latest Redux filter values.
  const currentFiltersRef = useRef(currentFilters);

  // Track the last known URL (pathname + search) so we can detect "no-op"
  // popstate events. A no-op happens when a modal/bottom sheet pushed a history
  // entry with the same URL (just different state) and is now being closed.
  // In that case the URL before and after the pop is identical, so we skip
  // the filter refetch entirely.
  const lastSearchUrl = useRef(
    typeof window !== "undefined" ? window.location.pathname + window.location.search : "",
  );

  // Keep refs in sync whenever Redux filters change (which happens after
  // applyFilter dispatches updateFilters).
  useEffect(() => {
    currentFiltersRef.current = currentFilters;
    if (typeof window !== "undefined") {
      lastSearchUrl.current = window.location.pathname + window.location.search;
    }
  }, [currentFilters]);

  // ---------------------------------------------------------------------------
  // Monkey-patch pushState / replaceState to keep lastSearchUrl in sync
  // SYNCHRONOUSLY. Without this, there's a timing gap between the URL changing
  // (via pushState in applyFilter) and the React useEffect updating the ref.
  // If the user presses back during that gap, lastSearchUrl would be stale and
  // the URL comparison could incorrectly skip a legitimate filter popstate.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const origPushState = window.history.pushState.bind(window.history);
    const origReplaceState = window.history.replaceState.bind(window.history);

    window.history.pushState = (state: any, title: string, url?: string | URL | null) => {
      origPushState(state, title, url);
      lastSearchUrl.current = window.location.pathname + window.location.search;
    };
    window.history.replaceState = (state: any, title: string, url?: string | URL | null) => {
      origReplaceState(state, title, url);
      lastSearchUrl.current = window.location.pathname + window.location.search;
    };

    return () => {
      window.history.pushState = origPushState;
      window.history.replaceState = origReplaceState;
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Initialize history state on mount if not already present.
  // On first page load, window.history.state is null. We set it to include the
  // current filters and canonical name so that future popstate events have
  // filter data to read from.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (window.history.state === null) {
      const searchParams = new URLSearchParams(window.location.search);
      const filters: any = {};

      for (const [key, value] of searchParams.entries()) {
        const isValidKey = filter_keys.includes(key);
        const isValidMoveIn = key === "move_in_month" ? isValidMoveInMonth(value) : true;

        if (isValidKey && isValidMoveIn) {
          filters[key] = value;
        }
      }

      const formattedFilters = formatFilterState(filters);
      // Match canonical name, accounting for optional language prefix
      // e.g. /fr/places/search/london or /places/search/london
      const pathMatch = window.location.pathname.match(/(?:\/[a-z]{2})?\/places\/search\/([^/]+)/);
      const canonicalName = pathMatch && pathMatch[1] ? pathMatch[1] : "";

      window.history.replaceState(
        { filters: formattedFilters, canonicalName },
        "",
        window.location.pathname + window.location.search,
      );
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Main popstate handler — fires on browser Back / Forward navigation.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Guard: prevent re-entrant calls
      if (isHandlingPopState.current) {
        return;
      }

      // Guard: only process popstate while the Search component is mounted.
      // The popstate listener is global, so it can fire after unmount (e.g.
      // user navigated to a property page and pressed back). Ignore those.
      if (!isMounted.current) {
        return;
      }

      // Guard: skip if the URL hasn't actually changed. This happens when a
      // modal or bottom sheet (e.g. "Lowest Price Guarantee") closes via
      // history.goBack(). The useModal hook pushes a history entry with the
      // SAME URL (just different state containing _modal/_modals). When that
      // entry is popped, the URL is identical — no filter changed, so we
      // skip to avoid an unnecessary API call and skeleton flicker.
      const currentSearchUrl = window.location.pathname + window.location.search;
      if (lastSearchUrl.current === currentSearchUrl) {
        return;
      }

      isHandlingPopState.current = true;

      // Close any open filter bottom sheets immediately to prevent visual flash
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent("CLOSE_FILTER_BOTTOM_SHEET"));
      });

      try {
        // -----------------------------------------------------------------
        // 1. Resolve the filters to apply from the popped history entry.
        //    Prefer filters stored in history state (set by applyFilter's
        //    pushState call). Fall back to parsing from the URL query string.
        // -----------------------------------------------------------------
        let filtersToApply: any = {};
        let canonicalNameFromState: string | null = null;

        if (event.state && event.state.filters !== undefined) {
          // Filters are in history state — use them directly
          filtersToApply = event.state.filters;
          canonicalNameFromState = event.state.canonicalName || null;
        } else {
          // No filter state in history — parse from URL query params
          const searchParams = new URLSearchParams(window.location.search);
          const filters: any = {};

          for (const [key, value] of searchParams.entries()) {
            const isValidKey = filter_keys.includes(key);
            const isValidMoveIn = key === "move_in_month" ? isValidMoveInMonth(value) : true;

            if (isValidKey && isValidMoveIn) {
              filters[key] = value;
            }
          }

          filtersToApply = formatFilterState(filters);

          // Extract canonical name from URL path
          const pathMatch = window.location.pathname.match(
            /(?:\/[a-z]{2})?\/places\/search\/([^/]+)/,
          );
          canonicalNameFromState = pathMatch && pathMatch[1] ? pathMatch[1] : null;
        }

        // -----------------------------------------------------------------
        // 2. Determine if university/locality filters need to be cleared.
        //    This affects localStorage and the canonical name used for fetch.
        // -----------------------------------------------------------------
        const previousFilters = currentFiltersRef.current;

        // Locality/university live in the path, not the query string. History
        // entries from search-bar navigations often omit them from state.filters,
        // so URL parsing looks like "geo cleared" and back-nav incorrectly loads
        // the parent city. Restore when we are still on the same geo path.
        if (!canonicalNameFromState) {
          const pathMatch = window.location.pathname.match(
            /(?:\/[a-z]{2})?\/places\/search\/([^/]+)/,
          );
          canonicalNameFromState = pathMatch && pathMatch[1] ? pathMatch[1] : null;
        }
        if (canonicalNameFromState && !filtersToApply.university && !filtersToApply.locality) {
          if (previousFilters?.locality === canonicalNameFromState) {
            filtersToApply = { ...filtersToApply, locality: canonicalNameFromState };
          } else if (previousFilters?.university === canonicalNameFromState) {
            filtersToApply = { ...filtersToApply, university: canonicalNameFromState };
          }
        }

        const hadUniversity = previousFilters.university;
        const hadLocality = previousFilters.locality;
        const hasUniversity = filtersToApply.university;
        const hasLocality = filtersToApply.locality;

        const clearUni = hadUniversity && !hasUniversity;
        const clearLocality = hadLocality && !hasLocality;

        // Clean up localStorage when university filter is removed via back nav
        if (clearUni && typeof window !== "undefined" && "localStorage" in window) {
          try {
            window.localStorage.removeItem("filtered_university_and_region");
          } catch {
            // Ignore browser privacy/security errors
          }
        }

        // Set localStorage when navigating back to a state that has university/locality
        // so other pages (e.g. BookForm) can read it on forward navigation
        if ((hasUniversity || hasLocality) && typeof window !== "undefined") {
          try {
            let label = null;
            if (hasUniversity) {
              // Try to find matching university label from cached recently visited list
              const locallyRecentlyVisited: any = JSON.parse(
                AmberlocalStorage.getItem("recentlyVisited") || "[]",
              );
              const uni = (locallyRecentlyVisited || []).find(
                (u: any) =>
                  u?.canonical_name === filtersToApply.university ||
                  u?.value === filtersToApply.university,
              );
              label = uni?.name || "";
            } else if (hasLocality) {
              label = filtersToApply.locality;
            }
            const locationStr = region && region.location ? getLocation(region.location) : "";
            if (label) {
              window.localStorage.setItem(
                "filtered_university_and_region",
                `${label} - ${locationStr}`,
              );
            }
          } catch (e) {
            // ignore localStorage errors
          }
        }

        // -----------------------------------------------------------------
        // 3. Attach canonical name for backward navigation so applyFilter
        //    can use it to determine the correct region/search endpoint.
        // -----------------------------------------------------------------
        if (canonicalNameFromState && !filtersToApply.university && !filtersToApply.locality) {
          filtersToApply._canonicalName = canonicalNameFromState;
        }

        // -----------------------------------------------------------------
        // 4. Dispatch the filter application (with skipHistoryPush=true so
        //    applyFilter doesn't push another history entry).
        // -----------------------------------------------------------------
        dispatch(applyFilter(filtersToApply, clearUni, clearLocality, true));

        // Update URL ref after dispatching so subsequent rapid popstates
        // compare against the correct baseline URL.
        lastSearchUrl.current = currentSearchUrl;
      } finally {
        // Reset the re-entry guard after a short delay to allow Redux state
        // updates to propagate before accepting the next popstate.
        setTimeout(() => {
          isHandlingPopState.current = false;
        }, 100);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [dispatch]);
};

export default useFilterHistory;
